import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Coins, Sparkles, ArrowDownToLine, Banknote, Clock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function CreatorEarnings() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/creator/earnings");
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load earnings");
    }
  };
  useEffect(() => { load(); }, []);

  const startConnect = async () => {
    setBusy(true);
    try {
      const r = await api.post("/creator/connect/start");
      if (r.data.onboarded) {
        toast.success("Already connected");
      } else {
        // Mocked: directly call complete for demo simplicity
        await api.post("/creator/connect/complete");
        toast.success("Connect onboarding complete ✦");
      }
      await load();
    } catch {
      toast.error("Connect failed");
    }
    setBusy(false);
  };

  const payout = async () => {
    setBusy(true);
    try {
      const r = await api.post("/creator/payout", {});
      toast.success(`Paid out $${r.data.amount.toFixed(2)} ✦`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payout failed");
    }
    setBusy(false);
  };

  if (!data) return <div className="text-[#64748B]">Loading earnings…</div>;

  const status = data.connect_status?.status;
  const connected = status === "active";

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-[#F5C542] mb-1">creator earnings</div>
        <h1 className="font-display text-4xl font-bold tracking-tight">Your money, on the network</h1>
        <p className="text-[#64748B] text-sm mt-1">10% platform fee. Direct payouts via Stripe Connect.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { l: "Available", v: `$${data.available.toFixed(2)}`, c: "#00E5FF" },
          { l: "Paid out", v: `$${data.paid_out.toFixed(2)}`, c: "#F5C542" },
          { l: "Lifetime gross", v: `$${data.lifetime_gross.toFixed(2)}`, c: "#0F172A" },
          { l: "Platform fees", v: `$${data.platform_fees.toFixed(2)}`, c: "#7B2CBF" },
        ].map((s) => (
          <div key={s.l} className="glass rounded-2xl p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#64748B]">{s.l}</div>
            <div className="font-display text-3xl font-bold mt-1" style={{ color: s.c }}>{s.v}</div>
          </div>
        ))}
      </div>

      {/* Connect status */}
      <div className="glass-heavy rounded-3xl p-5 grain">
        <div className="flex items-center gap-3 mb-3">
          <Banknote size={18} className="text-[#00E5FF]" />
          <div className="font-display font-bold">Stripe Connect</div>
          {connected ? (
            <span className="ml-auto text-xs glass-active px-3 py-1 rounded-full flex items-center gap-1 text-[#00E5FF]"><CheckCircle2 size={12} /> Active</span>
          ) : (
            <span className="ml-auto text-xs glass px-3 py-1 rounded-full text-[#64748B] flex items-center gap-1"><Clock size={12} /> Not connected</span>
          )}
        </div>
        {connected ? (
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="text-sm text-[#64748B]">
              Account: <span className="text-[#0F172A] font-mono text-xs">{data.connect_status.account_id}</span>
            </div>
            <button data-testid="payout-btn" onClick={payout} disabled={busy || data.available <= 0} className="nexus-btn-primary text-sm flex items-center gap-2">
              <ArrowDownToLine size={14} /> Payout ${data.available.toFixed(2)}
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <p className="text-sm text-[#64748B]">Connect a Stripe account to receive direct payouts. Demo onboarding completes instantly.</p>
            <button data-testid="connect-btn" onClick={startConnect} disabled={busy} className="nexus-btn-primary text-sm flex items-center gap-2">
              <Sparkles size={14} /> Connect Stripe
            </button>
          </div>
        )}
      </div>

      {/* Ledger */}
      <div className="glass-heavy rounded-3xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Coins size={18} className="text-[#F5C542]" />
          <div className="font-display font-bold">Earnings ledger</div>
        </div>
        {data.entries.length === 0 ? (
          <div className="text-sm text-[#64748B]">No earnings yet. Sell a product, accept a tip, or land a subscriber.</div>
        ) : (
          <div className="overflow-x-auto" data-testid="earnings-ledger">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[#64748B] text-xs uppercase tracking-[0.2em]">
                  <th className="text-left py-2">Date</th>
                  <th className="text-left py-2">Kind</th>
                  <th className="text-right py-2">Gross</th>
                  <th className="text-right py-2">Fee</th>
                  <th className="text-right py-2">Net</th>
                  <th className="text-right py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.entries.map((e, i) => (
                  <motion.tr key={e.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }} className="border-t border-slate-100">
                    <td className="py-2 text-[#64748B]">{new Date(e.created_at).toLocaleDateString()}</td>
                    <td className="py-2 capitalize">{e.kind}</td>
                    <td className="py-2 text-right">${e.gross.toFixed(2)}</td>
                    <td className="py-2 text-right text-[#7B2CBF]">-${e.platform_fee.toFixed(2)}</td>
                    <td className="py-2 text-right text-[#00E5FF] font-bold">${e.net.toFixed(2)}</td>
                    <td className="py-2 text-right text-xs">
                      <span className={`px-2 py-1 rounded-full ${e.status === "available" ? "bg-[#00E5FF]/15 text-[#00E5FF]" : "bg-[#F5C542]/15 text-[#F5C542]"}`}>
                        {e.status === "available" ? "available" : "paid out"}
                      </span>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
